import {
  Box,
  Breadcrumb,
  HStack,
  Link,
  useBreakpointValue,
} from "@chakra-ui/react"
import type { ReactNode } from "react"
import { Fragment } from "react"

export interface BreadcrumbNode {
  label: string
  href?: string
  element?: ReactNode
  isCurrent?: boolean
}

interface PageBreadcrumbsProps {
  items?: BreadcrumbNode[]
}

export function PageBreadcrumbs({ items }: PageBreadcrumbsProps) {
  if (!items || items.length === 0) {
    return null
  }

  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false

  if (isMobile) {
    return <ChipBreadcrumbs items={items} />
  }

  return <DesktopBreadcrumbs items={items} />
}

function DesktopBreadcrumbs({ items }: PageBreadcrumbsProps) {
  if (!items) return null

  return (
    <Breadcrumb.Root>
      <Breadcrumb.List>
        {items.map((item, index) => {
          const isLast = index === items.length - 1
          const isCurrent =
            item.isCurrent ?? (!item.href && !item.element && isLast)

          return (
            <Fragment key={`${item.label}-${index}`}>
              <Breadcrumb.Item>
                {isCurrent ? (
                  <Breadcrumb.CurrentLink>{item.label}</Breadcrumb.CurrentLink>
                ) : item.element ? (
                  item.element
                ) : item.href ? (
                  <Breadcrumb.Link href={item.href}>
                    {item.label}
                  </Breadcrumb.Link>
                ) : (
                  <Breadcrumb.Link>{item.label}</Breadcrumb.Link>
                )}
              </Breadcrumb.Item>
              {!isLast && <Breadcrumb.Separator />}
            </Fragment>
          )
        })}
      </Breadcrumb.List>
    </Breadcrumb.Root>
  )
}

function ChipBreadcrumbs({ items }: PageBreadcrumbsProps) {
  if (!items) return null

  return (
    <HStack
      data-testid="breadcrumb-chips"
      role="navigation"
      aria-label="breadcrumb"
      py={1}
      gap={2}
      flexWrap="wrap"
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1
        const isCurrent =
          item.isCurrent ?? (!item.href && !item.element && isLast)

        return (
          <BreadcrumbChip
            key={`${item.label}-${index}`}
            item={item}
            isCurrent={isCurrent}
          />
        )
      })}
    </HStack>
  )
}

interface BreadcrumbChipProps {
  item: BreadcrumbNode
  isCurrent: boolean
}

function BreadcrumbChip({ item, isCurrent }: BreadcrumbChipProps) {
  const sharedStyles = {
    px: 3,
    py: 1,
    rounded: "full",
    borderWidth: isCurrent ? 0 : "1px",
    borderColor: "gray.200",
    bg: isCurrent ? "gray.900" : "gray.100",
    color: isCurrent ? "white" : "gray.700",
    fontWeight: isCurrent ? "semibold" : "medium",
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
  }

  if (item.element) {
    return (
      <Box {...sharedStyles} aria-current={isCurrent ? "page" : undefined}>
        {item.element}
      </Box>
    )
  }

  if (item.href) {
    return (
      <Link
        href={item.href}
        {...sharedStyles}
        aria-current={isCurrent ? "page" : undefined}
      >
        {item.label}
      </Link>
    )
  }

  return (
    <Box {...sharedStyles} aria-current={isCurrent ? "page" : undefined}>
      {item.label}
    </Box>
  )
}
